import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState<'forward' | 'back'>('forward');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Auth guard
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/criar-conta', { replace: true });
        return;
      }
      // Check if user already has a username
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (profile?.username) {
        navigate('/auth/callback', { replace: true });
        return;
      }
      setAuthLoading(false);
    })();
  }, [navigate]);

  // Username validation + availability check
  const checkUsername = useCallback((value: string) => {
    if (value.length === 0) {
      setAvailable(null);
      setUsernameError(null);
      setChecking(false);
      return;
    }
    if (value.length < 3) {
      setAvailable(null);
      setUsernameError('mínimo 3 caracteres.');
      setChecking(false);
      return;
    }
    if (!/^[a-z][a-z0-9-]*$/.test(value)) {
      setAvailable(null);
      setUsernameError('só letras minúsculas, números e hífen.');
      setChecking(false);
      return;
    }
    setUsernameError(null);
    setChecking(true);
    supabase
      .from('user_profiles')
      .select('username')
      .eq('username', value)
      .maybeSingle()
      .then(({ data }) => {
        setChecking(false);
        setAvailable(!data);
      });
  }, []);

  useEffect(() => {
    setAvailable(null);
    setUsernameError(null);
    setChecking(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (username.length > 0) {
      debounceRef.current = setTimeout(() => checkUsername(username), 500);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [username, checkUsername]);

  const goToStep2 = () => {
    setSlideDir('forward');
    setAnimating(true);
    setTimeout(() => { setStep(2); setAnimating(false); }, 200);
  };

  const goToStep1 = () => {
    setSlideDir('back');
    setAnimating(true);
    setTimeout(() => { setStep(1); setAnimating(false); }, 200);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: existing } = await supabase
        .from('user_profiles')
        .select('user_id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      let error = null;

      if (existing) {
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            username: username,
            site_name: displayName,
          })
          .eq('user_id', session.user.id);

        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: session.user.id,
            username: username,
            site_name: displayName,
          });

        error = insertError;
      }

      if (!error) {
        // Fire and forget onboarding email
        supabase.functions.invoke('send-transactional-email', {
          body: {
            type: 'onboarding_complete',
            data: {
              to: session.user.email,
              username: username,
              displayName: displayName,
            },
          },
        }).catch(() => {});

        const { data: { session: currentSession } } = await supabase.auth.getSession();

        const isPreview = window.location.hostname.includes('lovable.app')
          || window.location.hostname === 'localhost';

        if (isPreview) {
          window.location.href = `/${username}/admin`;
        } else {
          const { data: magicData, error: magicError } = await supabase.functions.invoke(
            'generate-magic-link',
            { body: { email: currentSession?.user.email, username } }
          );

          if (!magicError && magicData?.url) {
            window.location.href = magicData.url;
          } else {
            // fallback: vai para o login do subdomínio
            window.location.href = `https://${username}.criaaa.com/login`;
          }
        }
      } else {
        console.error('Erro ao salvar perfil:', error);
        setError('erro ao salvar. tenta de novo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--lp-bg)',
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '13px',
        color: 'var(--lp-muted)',
      }}>
        carregando...
      </div>
    );
  }

  const isStep1Valid = username.length >= 3 && available === true && !usernameError;

  const getStepStyle = (isActive: boolean): React.CSSProperties => {
    if (animating) {
      return {
        opacity: 0,
        transform: slideDir === 'forward' ? 'translateX(-20px)' : 'translateX(20px)',
        transition: 'opacity 200ms ease, transform 200ms ease',
        position: 'absolute',
        width: '100%',
      };
    }
    return {
      opacity: isActive ? 1 : 0,
      transform: isActive ? 'translateX(0)' : (slideDir === 'forward' ? 'translateX(20px)' : 'translateX(-20px)'),
      transition: 'opacity 300ms ease, transform 300ms ease',
      position: isActive ? 'relative' : 'absolute',
      pointerEvents: isActive ? 'auto' : 'none',
      width: '100%',
    };
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '3rem 2rem',
      background: 'var(--lp-bg)',
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      {/* Logo */}
      <a href="https://criaaa.com" style={{
        position: 'fixed', top: '1.5rem', left: '2.5rem',
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '1rem', fontWeight: 400,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        textDecoration: 'none', color: 'var(--lp-ink)',
        zIndex: 10,
      }}>
        CRIAAA
      </a>

      {/* Progress bar */}
      <div style={{
        position: 'fixed', top: '4rem', left: 0, right: 0,
        display: 'flex', gap: 0, height: '1px', zIndex: 10,
      }}>
        <div style={{
          flex: 1, height: '1px',
          background: 'var(--lp-ink)',
          transition: 'background 0.6s cubic-bezier(0.16,1,0.3,1)',
        }} />
        <div style={{
          flex: 1, height: '1px',
          background: step >= 2 ? 'var(--lp-ink)' : 'var(--lp-border)',
          transition: 'background 0.6s cubic-bezier(0.16,1,0.3,1)',
        }} />
      </div>

      {/* Content */}
      <div style={{ maxWidth: 480, width: '100%', position: 'relative' }}>
        {/* Step 1 */}
        <div style={getStepStyle(step === 1 && !animating)}>
          <div style={{
            fontSize: 'var(--type-caption)', letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--lp-muted)',
            marginBottom: '2rem',
          }}>
            ETAPA 1 DE 2
          </div>

          <h1 style={{
            fontSize: 'var(--type-h1)', fontWeight: 300,
            lineHeight: 1.2, marginBottom: '0.5rem',
            color: 'var(--lp-ink)',
          }}>
            Qual vai ser seu endereço?
          </h1>

          <p style={{
            fontSize: 'var(--type-caption)', color: 'var(--lp-muted)',
            marginBottom: '2.5rem',
          }}>
            você pode mudar isso depois nas configurações.
          </p>

          {/* Username input */}
          <div style={{
            display: 'flex',
            border: '0.5px solid var(--lp-border2)',
            width: '100%',
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 'var(--type-body)',
              color: 'var(--lp-muted)',
              padding: '0.85rem 0 0.85rem 1.25rem',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}>
              criaaa.com/
            </span>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="seunome"
              autoFocus
              style={{
                flex: 1,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 'var(--type-body)',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '0.85rem 1.25rem 0.85rem 0',
                color: 'var(--lp-ink)',
                borderRadius: 0,
              }}
            />
          </div>

          {/* Feedback */}
          <div style={{
            fontSize: 'var(--type-caption)',
            marginTop: '0.5rem',
            minHeight: '1.4em',
          }}>
            {checking && (
              <span style={{ color: 'var(--lp-muted)' }}>verificando...</span>
            )}
            {!checking && available === true && !usernameError && (
              <span style={{ color: 'var(--lp-ink)' }}>✓ disponível</span>
            )}
            {!checking && available === false && !usernameError && (
              <span style={{ color: '#C0392B' }}>já está em uso.</span>
            )}
            {!checking && usernameError && (
              <span style={{ color: '#C0392B' }}>{usernameError}</span>
            )}
          </div>

          <button
            className="btn-fill"
            disabled={!isStep1Valid}
            onClick={goToStep2}
            style={{
              width: '100%',
              marginTop: '1.5rem',
              opacity: isStep1Valid ? 1 : 0.4,
              cursor: isStep1Valid ? 'pointer' : 'not-allowed',
              borderRadius: 0,
            }}
          >
            PRÓXIMO →
          </button>
        </div>

        {/* Step 2 */}
        <div style={getStepStyle(step === 2 && !animating)}>
          <div style={{
            fontSize: 'var(--type-caption)', letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--lp-muted)',
            marginBottom: '2rem',
          }}>
            ETAPA 2 DE 2
          </div>

          <h1 style={{
            fontSize: 'var(--type-h1)', fontWeight: 300,
            lineHeight: 1.2, marginBottom: '0.5rem',
            color: 'var(--lp-ink)',
          }}>
            Como você quer ser chamado?
          </h1>

          <p style={{
            fontSize: 'var(--type-caption)', color: 'var(--lp-muted)',
            marginBottom: '2.5rem',
          }}>
            esse nome aparece no seu portfólio público.
          </p>

          <input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder="nome ou nome do estúdio"
            autoFocus
            style={{
              width: '100%',
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 'var(--type-body)',
              border: '0.5px solid var(--lp-border2)',
              padding: '0.85rem 1.25rem',
              background: 'transparent',
              color: 'var(--lp-ink)',
              outline: 'none',
              borderRadius: 0,
            }}
          />

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1.5rem',
          }}>
            <span
              onClick={goToStep1}
              style={{
                fontSize: 'var(--type-caption)',
                color: 'var(--lp-muted)',
                cursor: 'pointer',
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              ← voltar
            </span>
          </div>

          <button
            className="btn-fill"
            disabled={!displayName.trim() || submitting}
            onClick={handleSubmit}
            style={{
              width: '100%',
              marginTop: '1rem',
              opacity: (!displayName.trim() || submitting) ? 0.4 : 1,
              cursor: (!displayName.trim() || submitting) ? 'not-allowed' : 'pointer',
              borderRadius: 0,
            }}
          >
            {submitting ? 'CRIANDO...' : 'CRIAR MEU PORTFÓLIO'}
          </button>
        </div>
      </div>
    </div>
  );
}
